// Cash Plan / Daily Money Target (Priority Pest Defense).
//
// Reads: monthly_expenses, monthlyBills, users, cashPlanEmployeeSettings,
// approvedInvoices, deposits, jobs, dailyLabor (Tech Daily Pay),
// subcontractorPay, cashPlanDebts, cashPlanBuckets.
//
// Payroll projection = each employee's normal day pay from the Staff page
// (getDefaultDailyPay: Daily Pay Rate, or Work Hourly Rate x Hours Per Day)
// x working days. The ACTUAL Tech Daily Pay saved this month is shown next to
// it for comparison and is never added on top of the projection.
//
// Money coming in = unpaid approvedInvoices (after deposits) + recurring /
// one-time visits still scheduled this month (utils/cashPlanIncome.js).

import React, { useEffect, useMemo, useState } from "react";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  serverTimestamp,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { db } from "../firebaseConfig";
import * as XLSX from "xlsx";
import "./CashPlanDashboard.css";
import {
  fetchDailyLaborDays,
  summarizeDailyLabor,
  usersToMap,
  getDefaultDailyPay,
  getHourlyPayRate,
  getHoursPerDay,
  isSubContractor,
  isDeactivated,
  staffName,
  roundMoney,
} from "../utils/techDailyPay";
import { projectScheduledVisits, unpaidInvoices } from "../utils/cashPlanIncome";

const ACCESS_CODE = "0417";
const UNLOCK_KEY = "cashPlanUnlocked";

const PAGE_INSTRUCTION_NOTE_STYLE = {
  border: "2px solid #dc2626",
  background: "#fff1f2",
  color: "#991b1b",
  borderRadius: 10,
  padding: 12,
  margin: "12px 0",
  fontWeight: 700,
  lineHeight: 1.45,
};

const cardStyle = {
  border: "1px solid #d1d5db",
  borderRadius: 12,
  padding: 14,
  background: "#fff",
  boxShadow: "0 1px 2px rgba(15,23,42,0.06)",
};

const sectionStyle = {
  ...cardStyle,
  marginTop: 14,
};

const inputStyle = {
  padding: "8px 10px",
  border: "1px solid #cbd5e1",
  borderRadius: 8,
  width: "100%",
  boxSizing: "border-box",
};

const buttonStyle = {
  padding: "9px 12px",
  borderRadius: 10,
  border: "1px solid #1d4ed8",
  background: "#2563eb",
  color: "#fff",
  fontWeight: 800,
  cursor: "pointer",
};

const grayButtonStyle = {
  ...buttonStyle,
  border: "1px solid #64748b",
  background: "#e2e8f0",
  color: "#0f172a",
};

const dangerButtonStyle = {
  ...buttonStyle,
  border: "1px solid #b91c1c",
  background: "#dc2626",
};

const bigNumber = { fontSize: 24, fontWeight: 900 };

const emptyDebtForm = {
  creditorName: "",
  debtType: "Credit Card",
  ownerType: "business",
  balance: "",
  minimumPayment: "",
  termFrequency: "Monthly",
  termPayments: "",
  extraPayment: "",
  targetPayoffDate: "",
  priority: "Medium",
  notes: "",
};

const emptyBucketForm = {
  bucketName: "Operating",
  bankName: "",
  accountLast4: "",
  purpose: "",
  targetPercent: "",
  ownerType: "business",
};

const emptyMonthlyForm = {
  vendorName: "",
  billType: "Utilities",
  ownerType: "business",
  amount: "",
  frequency: "Monthly",
  startDate: new Date().toISOString().slice(0, 10),
  description: "",
  paymentStopped: false,
  lastPaymentDate: "",
};

const frequencyOptions = ["Weekly", "Bi-Weekly", "Semi-Monthly", "Monthly", "Quarterly", "Annually", "One Time"];
const ownerOptions = ["business", "personal"];
const debtTypes = ["Credit Card", "Loan", "Vendor Balance", "Tax Debt", "Personal Debt", "Other"];
const billTypes = [
  "Utilities",
  "Insurance",
  "Loan",
  "Lease",
  "Rent",
  "Vehicle",
  "Fuel",
  "Phone",
  "Internet",
  "Software",
  "Subscriptions",
  "Credit Card",
  "Bank Fee",
  "Office Supplies",
  "Chemicals / Supplies",
  "Equipment Rental",
  "Storage",
  "Payroll",
  "Taxes",
  "Licensing",
  "Permits",
  "Marketing",
  "Legal",
  "Accounting",
  "Personal Bills",
  "Other",
];

const pad = (n) => String(n).padStart(2, "0");
const localKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const todayKey = () => localKey(new Date());

const toNum = (value) => {
  const n = Number(String(value ?? "").replace(/[^0-9.-]/g, ""));
  return Number.isFinite(n) ? n : 0;
};

const money = (value) =>
  toNum(value).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });

const toDateKey = (value) => {
  if (!value) return "";
  if (value?.toDate) return localKey(value.toDate());
  if (value instanceof Date) return localKey(value);
  if (typeof value === "object" && value.seconds) return localKey(new Date(value.seconds * 1000));
  return String(value).slice(0, 10);
};

const ownerLabel = (value) => (String(value || "business").toLowerCase() === "personal" ? "Personal" : "Business");

const monthlyAmountFromFrequency = (amount, frequency = "Monthly") => {
  const amt = toNum(amount);
  const freq = String(frequency || "Monthly").toLowerCase();
  if (!amt) return 0;
  if (freq === "weekly") return (amt * 52) / 12;
  if (freq === "bi-weekly" || freq === "biweekly") return (amt * 26) / 12;
  if (freq === "semi-monthly" || freq === "semimonthly") return amt * 2;
  if (freq === "quarterly") return amt / 3;
  if (freq === "annually" || freq === "annual" || freq === "yearly") return amt / 12;
  if (freq === "one time" || freq === "one-time") return 0;
  return amt;
};

const daysUntil = (targetDate) => {
  if (!targetDate) return 0;
  const start = new Date(`${todayKey()}T00:00:00`);
  const end = new Date(`${targetDate}T00:00:00`);
  if (Number.isNaN(end.getTime())) return 0;
  return Math.max(1, Math.ceil((end - start) / 86400000));
};

const debtTermFrequencyLabel = (value = "Monthly") =>
  String(value || "Monthly").toLowerCase().startsWith("week") ? "Weekly" : "Monthly";

const debtScheduledPayment = (debt = {}) => {
  const balance = toNum(debt.balance);
  const termPayments = toNum(debt.termPayments || debt.numberOfPayments || debt.termCount);
  const extraPayment = toNum(debt.extraPayment || debt.additionalPayment);
  const fallbackMinimum = toNum(debt.minimumPayment);
  if (balance > 0 && termPayments > 0) return balance / termPayments + extraPayment;
  return fallbackMinimum + extraPayment;
};

const debtPayoffDailyNeeded = (debt = {}) => {
  const balance = toNum(debt.balance);
  const targetDate = toDateKey(debt.targetPayoffDate);
  if (!balance || !targetDate) return 0;
  return balance / daysUntil(targetDate);
};

const debtMonthlyEquivalent = (debt = {}, workingDays = 22) => {
  const targetDaily = debtPayoffDailyNeeded(debt);
  // With a target payoff date, reserve enough per day to hit that date.
  // Otherwise use the scheduled term/minimum payment.
  if (targetDaily > 0) return targetDaily * Math.max(1, toNum(workingDays) || 22);
  const payment = debtScheduledPayment(debt);
  const frequency = debtTermFrequencyLabel(debt.termFrequency || debt.paymentFrequency);
  if (frequency === "Weekly") return (payment * 52) / 12;
  return payment;
};

const debtDailyEquivalent = (debt = {}, workingDays = 22) => {
  const targetDaily = debtPayoffDailyNeeded(debt);
  if (targetDaily > 0) return targetDaily;
  return debtMonthlyEquivalent(debt, workingDays) / Math.max(1, toNum(workingDays) || 22);
};

const businessDaysBetween = (startKey, endKey) => {
  const cursor = new Date(`${startKey}T00:00:00`);
  const final = new Date(`${endKey}T00:00:00`);
  let count = 0;
  while (cursor <= final) {
    const day = cursor.getDay();
    if (day !== 0 && day !== 6) count += 1;
    cursor.setDate(cursor.getDate() + 1);
  }
  return count;
};

const startOfCurrentMonth = () => {
  const now = new Date();
  return localKey(new Date(now.getFullYear(), now.getMonth(), 1));
};

const endOfCurrentMonth = () => {
  const now = new Date();
  return localKey(new Date(now.getFullYear(), now.getMonth() + 1, 0));
};

const remainingBusinessDaysThisMonth = () => Math.max(1, businessDaysBetween(todayKey(), endOfCurrentMonth()));

const paymentIsActiveForPlan = (item = {}) => {
  const stopped = item.paymentStopped === true || item.stopPayment === true || item.status === "Stopped";
  if (!stopped) return true;
  const lastPaymentDate = toDateKey(item.lastPaymentDate || item.endDate || item.stopPaymentDate);
  if (!lastPaymentDate) return false;
  return lastPaymentDate >= todayKey();
};

const normalizeMonthlyBill = (item = {}, sourceCollection = "monthly_expenses") => {
  const vendorName = item.vendorName || item.vendor || item.companyName || item.name || "";
  const billType = item.billType || item.category || item.type || "Other";
  const frequency = item.frequency || item.recurringFrequency || "Monthly";
  const ownerType = String(item.ownerType || item.classification || item.businessOrPersonal || "business").toLowerCase() === "personal" ? "personal" : "business";
  const amount = toNum(item.amount || item.total || item.cost);
  const activeInCashPlan = paymentIsActiveForPlan(item);
  const baseMonthlyEquivalent = monthlyAmountFromFrequency(amount, frequency);

  return {
    ...item,
    vendorName,
    billType,
    frequency,
    ownerType,
    startDate: toDateKey(item.startDate || item.date || item.billDate || item.dueDate),
    amount,
    sourceCollection,
    paymentStopped: item.paymentStopped === true || item.stopPayment === true || item.status === "Stopped",
    lastPaymentDate: toDateKey(item.lastPaymentDate || item.endDate || item.stopPaymentDate),
    activeInCashPlan,
    baseMonthlyEquivalent,
    monthlyEquivalent: activeInCashPlan ? baseMonthlyEquivalent : 0,
  };
};

/** Day pay used for the projection: override hourly x hours/day, else Staff day pay. */
const projectedDailyPay = (user = {}, overrideHourly = 0) =>
  overrideHourly > 0 ? roundMoney(overrideHourly * getHoursPerDay(user)) : getDefaultDailyPay(user);

const downloadWorkbook = (sheets, filename) => {
  const wb = XLSX.utils.book_new();
  Object.entries(sheets).forEach(([sheetName, rows]) => {
    const ws = XLSX.utils.json_to_sheet(rows.length ? rows : [{ Note: "No records found." }]);
    XLSX.utils.book_append_sheet(wb, ws, sheetName.slice(0, 31));
  });
  XLSX.writeFile(wb, filename);
};

const invoiceAmount = (invoice = {}) => toNum(invoice.amount || invoice.price || invoice.rate || invoice.invoiceAmount || invoice.total);
const invoiceDate = (invoice = {}) =>
  toDateKey(invoice.invoiceDate || invoice.dateOfService || invoice.serviceDate || invoice.sentAt || invoice.createdAt || invoice.date);

const readUnlocked = () => {
  try {
    return localStorage.getItem(UNLOCK_KEY) === "true";
  } catch {
    return false;
  }
};

export default function CashPlanDashboard() {
  const [unlocked, setUnlocked] = useState(readUnlocked);
  const [accessInput, setAccessInput] = useState("");
  const [accessError, setAccessError] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [monthlyExpenses, setMonthlyExpenses] = useState([]);
  const [subcontractorPayments, setSubcontractorPayments] = useState([]);
  const [monthlyBills, setMonthlyBills] = useState([]);
  const [users, setUsers] = useState([]);
  const [employeeSettings, setEmployeeSettings] = useState([]);
  const [approvedInvoices, setApprovedInvoices] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [laborDays, setLaborDays] = useState([]);
  const [debts, setDebts] = useState([]);
  const [buckets, setBuckets] = useState([]);

  const [debtForm, setDebtForm] = useState(emptyDebtForm);
  const [bucketForm, setBucketForm] = useState(emptyBucketForm);
  const [monthlyForm, setMonthlyForm] = useState(emptyMonthlyForm);
  const [workingDays, setWorkingDays] = useState(22);
  const [todayMoney, setTodayMoney] = useState("");

  // All detail containers start hidden so the page opens clean.
  const [showExcelTools, setShowExcelTools] = useState(false);
  const [showBuckets, setShowBuckets] = useState(false);
  const [showMonthly, setShowMonthly] = useState(false);
  const [showPayroll, setShowPayroll] = useState(false);
  const [showHiddenEmployees, setShowHiddenEmployees] = useState(false);
  const [showDebts, setShowDebts] = useState(false);
  const [showIncome, setShowIncome] = useState(false);

  const [editDrafts, setEditDrafts] = useState({});
  const [bulkPreview, setBulkPreview] = useState({ debts: [], buckets: [], monthly: [] });
  const [bulkStatus, setBulkStatus] = useState("");

  const loadData = async () => {
    if (!unlocked) return;
    setLoading(true);
    try {
      const [
        monthlyExpenseSnap,
        monthlyBillsSnap,
        usersSnap,
        employeeSettingsSnap,
        approvedInvoicesSnap,
        debtsSnap,
        bucketsSnap,
        subcontractorPaySnap,
        depositsSnap,
        jobsSnap,
        days,
      ] = await Promise.all([
        getDocs(collection(db, "monthly_expenses")),
        getDocs(collection(db, "monthlyBills")),
        getDocs(collection(db, "users")),
        getDocs(collection(db, "cashPlanEmployeeSettings")),
        getDocs(collection(db, "approvedInvoices")),
        getDocs(collection(db, "cashPlanDebts")),
        getDocs(collection(db, "cashPlanBuckets")),
        getDocs(collection(db, "subcontractorPay")),
        getDocs(collection(db, "deposits")),
        getDocs(collection(db, "jobs")),
        fetchDailyLaborDays(startOfCurrentMonth(), endOfCurrentMonth()).catch((err) => {
          console.error("Could not load Tech Daily Pay:", err);
          return [];
        }),
      ]);
      const plain = (snap) => snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setMonthlyExpenses(plain(monthlyExpenseSnap));
      setMonthlyBills(plain(monthlyBillsSnap));
      setUsers(plain(usersSnap));
      setEmployeeSettings(plain(employeeSettingsSnap));
      setApprovedInvoices(plain(approvedInvoicesSnap));
      setDebts(plain(debtsSnap));
      setBuckets(plain(bucketsSnap));
      setSubcontractorPayments(plain(subcontractorPaySnap));
      setDeposits(plain(depositsSnap));
      setJobs(plain(jobsSnap));
      setLaborDays(days);
    } catch (err) {
      console.error("Could not load the cash plan:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unlocked]);

  const unlock = (e) => {
    e.preventDefault();
    if (String(accessInput).trim() === ACCESS_CODE) {
      try {
        localStorage.setItem(UNLOCK_KEY, "true");
      } catch {
        /* private window — stays unlocked for this visit only */
      }
      setUnlocked(true);
      setAccessError("");
      setAccessInput("");
      return;
    }
    setAccessError("Incorrect code.");
  };

  const lockPage = () => {
    try {
      localStorage.removeItem(UNLOCK_KEY);
    } catch {
      /* ignore */
    }
    setUnlocked(false);
  };

  const setDraftValue = (key, field, value) => {
    setEditDrafts((prev) => ({
      ...prev,
      [key]: {
        ...(prev[key] || {}),
        [field]: value,
      },
    }));
  };

  const draftValue = (key, item, field, fallback = "") => {
    if (editDrafts[key] && Object.prototype.hasOwnProperty.call(editDrafts[key], field)) return editDrafts[key][field];
    return item?.[field] ?? fallback;
  };

  const clearDraft = (key) => {
    setEditDrafts((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const monthStart = startOfCurrentMonth();
  const monthEnd = endOfCurrentMonth();
  const today = todayKey();
  const monthKey = monthStart.slice(0, 7);

  const allMonthlyBills = useMemo(() => {
    const newer = monthlyExpenses.map((m) => normalizeMonthlyBill(m, "monthly_expenses"));
    const older = monthlyBills.map((m) => normalizeMonthlyBill(m, "monthlyBills"));
    const seen = new Set();
    return [...newer, ...older]
      .filter((item) => {
        const key = item.sourceCashPlanId || `${item.vendorName}|${item.billType}|${item.amount}|${item.frequency}|${item.ownerType}`.toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .sort((a, b) => String(a.vendorName || "").localeCompare(String(b.vendorName || "")));
  }, [monthlyExpenses, monthlyBills]);

  const employeeSettingsById = useMemo(() => {
    const map = {};
    employeeSettings.forEach((setting) => {
      if (setting.employeeId) map[setting.employeeId] = setting;
      if (setting.id) map[setting.id] = setting;
    });
    return map;
  }, [employeeSettings]);

  const usersById = useMemo(() => usersToMap(users), [users]);

  // ACTUAL Tech Daily Pay saved this month (through today).
  const dailyPayMonth = useMemo(
    () => summarizeDailyLabor(laborDays, usersById, monthStart, today),
    [laborDays, usersById, monthStart, today]
  );
  const actualTechPayMTD = dailyPayMonth.byMonth[monthKey] || 0;
  const businessDaysSoFar = Math.max(0, businessDaysBetween(monthStart, today));

  const allPayrollEmployees = useMemo(() => {
    const workdays = Math.max(1, toNum(workingDays) || 22);
    return users
      .filter((user) => !isSubContractor(user))
      .map((user) => {
        const setting = employeeSettingsById[user.id] || {};
        const defaultInactive = isDeactivated(user) || !!user.terminationDate;
        const hidden = setting.hidden !== undefined ? setting.hidden === true : defaultInactive;
        const overrideHourlyRate = toNum(setting.overrideHourlyRate || setting.hourlyRateOverride);
        const staffHourlyRate = getHourlyPayRate(user);
        const dailyPay = projectedDailyPay(user, overrideHourlyRate);
        const monthlyPayroll = dailyPay * workdays;
        const actual = dailyPayMonth.byUser[user.id] || { days: 0, hours: 0, amount: 0 };
        return {
          id: user.id,
          name: staffName(user) || "Unnamed Employee",
          staffHourlyRate,
          overrideHourlyRate,
          hourlyRate: overrideHourlyRate > 0 ? overrideHourlyRate : staffHourlyRate,
          hoursPerDay: getHoursPerDay(user),
          flatDailyRate: toNum(user.dailyPayRate),
          dailyPay,
          monthlyPayroll,
          projectedMTD: hidden ? 0 : dailyPay * businessDaysSoFar,
          actualDays: actual.days,
          actualMTD: actual.amount,
          hidden,
          payrollStatus: hidden ? "Hidden" : "Active",
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [users, employeeSettingsById, workingDays, dailyPayMonth, businessDaysSoFar]);

  const payrollEmployees = useMemo(
    () => allPayrollEmployees.filter((emp) => showHiddenEmployees || !emp.hidden),
    [allPayrollEmployees, showHiddenEmployees]
  );

  const activePayrollEmployees = useMemo(() => allPayrollEmployees.filter((emp) => !emp.hidden), [allPayrollEmployees]);

  const currentMonthInvoices = useMemo(() => {
    return approvedInvoices.filter((invoice) => {
      const date = invoiceDate(invoice);
      if (!date || date < monthStart || date > monthEnd) return false;
      const status = String(invoice.status || "").toLowerCase();
      return !["canceled", "cancelled", "void", "deleted", "draft"].includes(status);
    });
  }, [approvedInvoices, monthStart, monthEnd]);

  const invoiceProgress = useMemo(() => {
    const total = currentMonthInvoices.reduce((sum, invoice) => sum + invoiceAmount(invoice), 0);
    return {
      total,
      count: currentMonthInvoices.length,
      remainingBusinessDays: remainingBusinessDaysThisMonth(),
    };
  }, [currentMonthInvoices]);

  // Money coming in.
  const income = useMemo(() => {
    const unpaid = unpaidInvoices(approvedInvoices, deposits).sort((a, b) => invoiceDate(a).localeCompare(invoiceDate(b)));
    const unpaidTotal = unpaid.reduce((s, inv) => s + inv.__balance, 0);
    const visits = projectScheduledVisits(jobs, today, monthEnd);
    const visitsTotal = visits.reduce((s, v) => s + v.amount, 0);
    const receivedThisMonth = deposits.reduce((s, d) => {
      const date = toDateKey(d.date);
      if (!date || date < monthStart || date > monthEnd || d.splitInvoiceOnly || d.invoiceOnly) return s;
      return s + toNum(d.amount);
    }, 0);
    return { unpaid, unpaidTotal, visits, visitsTotal, receivedThisMonth, expected: unpaidTotal + visitsTotal };
  }, [approvedInvoices, deposits, jobs, today, monthStart, monthEnd]);

  const subcontractorPaidThisMonth = useMemo(() => {
    return subcontractorPayments.reduce((sum, payment) => {
      const date = toDateKey(payment.date || payment.datePaid || payment.createdAt);
      if (!date || date < monthStart || date > monthEnd) return sum;
      return sum + toNum(payment.amount ?? payment.amountPaid);
    }, 0);
  }, [subcontractorPayments, monthStart, monthEnd]);

  const totals = useMemo(() => {
    const byOwner = {
      business: { monthlyBills: 0, debtMonthly: 0, debtDaily: 0 },
      personal: { monthlyBills: 0, debtMonthly: 0, debtDaily: 0 },
    };

    allMonthlyBills.forEach((bill) => {
      byOwner[bill.ownerType || "business"].monthlyBills += bill.monthlyEquivalent;
    });

    debts.forEach((debt) => {
      const ownerType = String(debt.ownerType || "business").toLowerCase() === "personal" ? "personal" : "business";
      byOwner[ownerType].debtMonthly += debtMonthlyEquivalent(debt, workingDays);
    });

    const workdays = Math.max(1, toNum(workingDays) || 22);
    const employeeMonthly = activePayrollEmployees.reduce((sum, emp) => sum + emp.monthlyPayroll, 0);
    const employeeDaily = employeeMonthly / workdays;
    const employeeProjectedMTD = activePayrollEmployees.reduce((sum, emp) => sum + emp.projectedMTD, 0);
    const businessMonthly = byOwner.business.monthlyBills + byOwner.business.debtMonthly;
    const personalMonthly = byOwner.personal.monthlyBills + byOwner.personal.debtMonthly;
    const businessDaily = businessMonthly / workdays + byOwner.business.debtDaily;
    const personalDaily = personalMonthly / workdays + byOwner.personal.debtDaily;
    const allMonthly = businessMonthly + personalMonthly + employeeMonthly;
    const allDaily = businessDaily + personalDaily + employeeDaily;
    const remainingMonthlyNeed = Math.max(0, allMonthly - invoiceProgress.total);
    const remainingDailyNeed = remainingMonthlyNeed / Math.max(1, invoiceProgress.remainingBusinessDays);
    const needAfterScheduled = Math.max(0, remainingMonthlyNeed - income.visitsTotal);

    return {
      byOwner,
      workdays,
      businessMonthly,
      personalMonthly,
      employeeMonthly,
      employeeProjectedMTD,
      allMonthly,
      businessDaily,
      personalDaily,
      employeeDaily,
      allDaily,
      remainingMonthlyNeed,
      remainingDailyNeed,
      needAfterScheduled,
    };
  }, [allMonthlyBills, activePayrollEmployees, debts, workingDays, invoiceProgress, income]);

  const addDebt = async (e) => {
    e.preventDefault();
    if (!debtForm.creditorName || !toNum(debtForm.balance)) {
      alert("Creditor name and balance are required.");
      return;
    }
    if (!window.confirm("Save this debt/payoff item to the private Cash Plan?")) return;
    setSaving(true);
    try {
      await addDoc(collection(db, "cashPlanDebts"), {
        ...debtForm,
        balance: toNum(debtForm.balance),
        minimumPayment: toNum(debtForm.minimumPayment),
        termFrequency: debtTermFrequencyLabel(debtForm.termFrequency),
        termPayments: toNum(debtForm.termPayments),
        extraPayment: toNum(debtForm.extraPayment),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      setDebtForm(emptyDebtForm);
      await loadData();
    } finally {
      setSaving(false);
    }
  };

  const addBucket = async (e) => {
    e.preventDefault();
    if (!bucketForm.bucketName) {
      alert("Bucket name is required.");
      return;
    }
    if (!window.confirm("Save this cash bucket/account allocation?")) return;
    setSaving(true);
    try {
      await addDoc(collection(db, "cashPlanBuckets"), {
        ...bucketForm,
        targetPercent: toNum(bucketForm.targetPercent),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      setBucketForm(emptyBucketForm);
      await loadData();
    } finally {
      setSaving(false);
    }
  };

  const addMonthlyBill = async (e) => {
    e.preventDefault();
    if (!monthlyForm.vendorName || !toNum(monthlyForm.amount)) {
      alert("Vendor and amount are required.");
      return;
    }
    if (!window.confirm("Save this monthly bill to the existing monthly bill sources?")) return;
    setSaving(true);
    try {
      const clean = {
        ...monthlyForm,
        amount: toNum(monthlyForm.amount),
        source: "CashPlanDashboard",
        sourceCashPlanId: `cashplan_${Date.now()}`,
      };

      await Promise.all([
        addDoc(collection(db, "monthly_expenses"), {
          vendorId: "",
          vendorName: clean.vendorName,
          billType: clean.billType,
          billTypeOther: clean.billType === "Other" ? clean.description : "",
          description: clean.description,
          amount: clean.amount,
          frequency: clean.frequency,
          startDate: clean.startDate || todayKey(),
          ownerType: clean.ownerType,
          paymentStopped: clean.paymentStopped === true,
          lastPaymentDate: clean.lastPaymentDate || "",
          source: clean.source,
          sourceCashPlanId: clean.sourceCashPlanId,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        }),
        addDoc(collection(db, "monthlyBills"), {
          vendor: clean.vendorName,
          amount: clean.amount,
          billType: clean.billType,
          description: clean.description,
          effect: "subtract",
          date: clean.startDate || todayKey(),
          frequency: clean.frequency,
          ownerType: clean.ownerType,
          paymentStopped: clean.paymentStopped === true,
          lastPaymentDate: clean.lastPaymentDate || "",
          source: clean.source,
          sourceCashPlanId: clean.sourceCashPlanId,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        }),
      ]);

      setMonthlyForm(emptyMonthlyForm);
      await loadData();
    } finally {
      setSaving(false);
    }
  };

  const updateLinkedMonthlyBills = async (bill, changes = {}) => {
    if (!bill?.id) return;
    const payloadForMonthlyExpenses = {
      ...(changes.vendorName !== undefined ? { vendorName: changes.vendorName } : {}),
      ...(changes.billType !== undefined ? { billType: changes.billType } : {}),
      ...(changes.ownerType !== undefined ? { ownerType: changes.ownerType } : {}),
      ...(changes.amount !== undefined ? { amount: toNum(changes.amount) } : {}),
      ...(changes.frequency !== undefined ? { frequency: changes.frequency } : {}),
      ...(changes.startDate !== undefined ? { startDate: changes.startDate } : {}),
      ...(changes.description !== undefined ? { description: changes.description } : {}),
      ...(changes.paymentStopped !== undefined ? { paymentStopped: changes.paymentStopped === true, status: changes.paymentStopped ? "Stopped" : "Active" } : {}),
      ...(changes.lastPaymentDate !== undefined ? { lastPaymentDate: changes.lastPaymentDate } : {}),
      updatedAt: serverTimestamp(),
    };
    const payloadForMonthlyBills = {
      ...(changes.vendorName !== undefined ? { vendor: changes.vendorName } : {}),
      ...(changes.billType !== undefined ? { billType: changes.billType } : {}),
      ...(changes.ownerType !== undefined ? { ownerType: changes.ownerType } : {}),
      ...(changes.amount !== undefined ? { amount: toNum(changes.amount) } : {}),
      ...(changes.frequency !== undefined ? { frequency: changes.frequency } : {}),
      ...(changes.startDate !== undefined ? { date: changes.startDate } : {}),
      ...(changes.description !== undefined ? { description: changes.description } : {}),
      ...(changes.paymentStopped !== undefined ? { paymentStopped: changes.paymentStopped === true, status: changes.paymentStopped ? "Stopped" : "Active" } : {}),
      ...(changes.lastPaymentDate !== undefined ? { lastPaymentDate: changes.lastPaymentDate } : {}),
      updatedAt: serverTimestamp(),
    };

    const linkedId = bill.sourceCashPlanId || "";
    const updates = [];
    if (linkedId) {
      monthlyExpenses.filter((item) => item.sourceCashPlanId === linkedId).forEach((item) => {
        updates.push(updateDoc(doc(db, "monthly_expenses", item.id), payloadForMonthlyExpenses));
      });
      monthlyBills.filter((item) => item.sourceCashPlanId === linkedId).forEach((item) => {
        updates.push(updateDoc(doc(db, "monthlyBills", item.id), payloadForMonthlyBills));
      });
    }

    if (!updates.length) {
      updates.push(
        updateDoc(
          doc(db, bill.sourceCollection || "monthly_expenses", bill.id),
          bill.sourceCollection === "monthlyBills" ? payloadForMonthlyBills : payloadForMonthlyExpenses
        )
      );
    }

    await Promise.all(updates);
    await loadData();
  };

  const saveMonthlyDraft = async (bill) => {
    const key = `monthly-${bill.sourceCollection}-${bill.id}`;
    const draft = editDrafts[key] || {};
    if (!Object.keys(draft).length) {
      alert("No monthly bill changes to save.");
      return;
    }
    if (!window.confirm("Save these monthly bill changes? This keeps the bill history and only changes the plan going forward.")) return;
    await updateLinkedMonthlyBills(bill, draft);
    clearDraft(key);
  };

  const stopMonthlyBill = async (bill) => {
    const key = `monthly-${bill.sourceCollection}-${bill.id}`;
    const draft = editDrafts[key] || {};
    const lastPaymentDate = draft.lastPaymentDate || bill.lastPaymentDate || todayKey();
    const nextStopped = !bill.paymentStopped;
    const message = nextStopped
      ? `Stop future payments for ${bill.vendorName || "this bill"}? It will remain in history and stop counting after ${lastPaymentDate}.`
      : `Restart ${bill.vendorName || "this bill"} so it counts again going forward?`;
    if (!window.confirm(message)) return;
    await updateLinkedMonthlyBills(bill, {
      paymentStopped: nextStopped,
      lastPaymentDate: nextStopped ? lastPaymentDate : "",
    });
    clearDraft(key);
  };

  const saveBucketDraft = async (bucket) => {
    const key = `bucket-${bucket.id}`;
    const draft = editDrafts[key] || {};
    if (!Object.keys(draft).length) {
      alert("No bucket changes to save.");
      return;
    }
    if (!window.confirm("Save these bucket/account allocation changes?")) return;
    await updateDoc(doc(db, "cashPlanBuckets", bucket.id), {
      ...(draft.bucketName !== undefined ? { bucketName: draft.bucketName } : {}),
      ...(draft.bankName !== undefined ? { bankName: draft.bankName } : {}),
      ...(draft.accountLast4 !== undefined ? { accountLast4: String(draft.accountLast4).replace(/\D/g, "").slice(-4) } : {}),
      ...(draft.ownerType !== undefined ? { ownerType: draft.ownerType } : {}),
      ...(draft.targetPercent !== undefined ? { targetPercent: toNum(draft.targetPercent) } : {}),
      ...(draft.purpose !== undefined ? { purpose: draft.purpose } : {}),
      updatedAt: serverTimestamp(),
    });
    clearDraft(key);
    await loadData();
  };

  const saveDebtDraft = async (debt) => {
    const key = `debt-${debt.id}`;
    const draft = editDrafts[key] || {};
    if (!Object.keys(draft).length) {
      alert("No debt changes to save.");
      return;
    }
    if (!window.confirm("Save these private debt/payoff changes?")) return;
    await updateDoc(doc(db, "cashPlanDebts", debt.id), {
      ...(draft.creditorName !== undefined ? { creditorName: draft.creditorName } : {}),
      ...(draft.debtType !== undefined ? { debtType: draft.debtType } : {}),
      ...(draft.ownerType !== undefined ? { ownerType: draft.ownerType } : {}),
      ...(draft.balance !== undefined ? { balance: toNum(draft.balance) } : {}),
      ...(draft.minimumPayment !== undefined ? { minimumPayment: toNum(draft.minimumPayment) } : {}),
      ...(draft.termFrequency !== undefined ? { termFrequency: debtTermFrequencyLabel(draft.termFrequency) } : {}),
      ...(draft.termPayments !== undefined ? { termPayments: toNum(draft.termPayments) } : {}),
      ...(draft.extraPayment !== undefined ? { extraPayment: toNum(draft.extraPayment) } : {}),
      ...(draft.targetPayoffDate !== undefined ? { targetPayoffDate: draft.targetPayoffDate } : {}),
      ...(draft.priority !== undefined ? { priority: draft.priority } : {}),
      ...(draft.notes !== undefined ? { notes: draft.notes } : {}),
      updatedAt: serverTimestamp(),
    });
    clearDraft(key);
    await loadData();
  };

  const saveEmployeeDraft = async (emp) => {
    const key = `emp-${emp.id}`;
    const draft = editDrafts[key] || {};
    if (!Object.keys(draft).length) {
      alert("No employee payroll changes to save.");
      return;
    }
    if (!window.confirm("Save these employee payroll reserve settings? This does not change the Staff page unless you edit Staff directly.")) return;
    await setDoc(
      doc(db, "cashPlanEmployeeSettings", emp.id),
      {
        employeeId: emp.id,
        ...(draft.overrideHourlyRate !== undefined ? { overrideHourlyRate: toNum(draft.overrideHourlyRate) } : {}),
        ...(draft.hidden !== undefined ? { hidden: draft.hidden === true || draft.hidden === "true" } : {}),
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
    clearDraft(key);
    await loadData();
  };

  const setEmployeeHiddenNow = async (emp, hidden) => {
    const action = hidden ? "hide this employee from payroll reserve" : "show this employee in payroll reserve";
    if (!window.confirm(`Are you sure you want to ${action}?`)) return;
    await setDoc(
      doc(db, "cashPlanEmployeeSettings", emp.id),
      {
        employeeId: emp.id,
        hidden: hidden === true,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
    clearDraft(`emp-${emp.id}`);
    await loadData();
  };

  const deleteDebt = async (id) => {
    if (!window.confirm("Delete this debt from the private cash plan?")) return;
    await deleteDoc(doc(db, "cashPlanDebts", id));
    await loadData();
  };

  const deleteBucket = async (id) => {
    if (!window.confirm("Delete this cash bucket?")) return;
    await deleteDoc(doc(db, "cashPlanBuckets", id));
    await loadData();
  };

  const downloadTemplate = () => {
    downloadWorkbook(
      {
        Debts: [
          {
            Creditor: "Example Card",
            "Debt Type": "Credit Card",
            "Personal or Business": "business",
            Balance: 5000,
            "Minimum Payment": 150,
            "Term Frequency": "Monthly",
            "Number of Payments": 48,
            "Extra Payment": 25,
            "Target Payoff Date": "2026-12-31",
            Priority: "High",
            Notes: "Sample row - replace with real debts",
          },
        ],
        "Bank Buckets": [
          {
            "Bucket Name": "Payroll Reserve",
            Bank: "Your Bank",
            "Last 4": "1234",
            "Personal or Business": "business",
            "Target Percent": 20,
            Purpose: "Hold payroll money before payroll date",
          },
        ],
        "Monthly Bills": [
          {
            Vendor: "Phone Company",
            "Bill Type": "Phone",
            "Personal or Business": "business",
            Amount: 250,
            Frequency: "Monthly",
            "Start Date": todayKey(),
            Description: "Sample monthly bill - replace or delete",
            "Stop Payment": "No",
            "Last Payment Date": "",
          },
        ],
      },
      "Cash_Plan_Bulk_Template.xlsx"
    );
  };

  const downloadPlan = () => {
    const summaryRows = [
      { Item: "Business monthly need", Amount: totals.businessMonthly },
      { Item: "Personal monthly need", Amount: totals.personalMonthly },
      { Item: "Employee monthly payroll (projected from Staff day pay)", Amount: totals.employeeMonthly },
      { Item: "Payroll projected month-to-date", Amount: totals.employeeProjectedMTD },
      { Item: "Tech Daily Pay actual month-to-date", Amount: actualTechPayMTD },
      { Item: "Subcontractor payments recorded this month", Amount: subcontractorPaidThisMonth },
      { Item: "Total monthly need", Amount: totals.allMonthly },
      { Item: "Business daily target", Amount: totals.businessDaily },
      { Item: "Personal daily target", Amount: totals.personalDaily },
      { Item: "Employee daily target", Amount: totals.employeeDaily },
      { Item: "Total daily target", Amount: totals.allDaily },
      { Item: "Invoices dated this month", Amount: invoiceProgress.total },
      { Item: "Remaining monthly target after this month's invoices", Amount: totals.remainingMonthlyNeed },
      { Item: "Remaining daily target this month", Amount: totals.remainingDailyNeed },
      { Item: "Scheduled visits rest of month", Amount: income.visitsTotal },
      { Item: "Still needed after scheduled visits", Amount: totals.needAfterScheduled },
      { Item: "Unpaid invoices (coming in)", Amount: income.unpaidTotal },
      { Item: "Payments received this month", Amount: income.receivedThisMonth },
      { Item: "Working days used", Amount: totals.workdays },
    ];

    downloadWorkbook(
      {
        Summary: summaryRows,
        "Monthly Bills": allMonthlyBills.map((bill) => ({
          Source: bill.sourceCollection,
          Vendor: bill.vendorName,
          "Bill Type": bill.billType,
          "Personal or Business": ownerLabel(bill.ownerType),
          Amount: bill.amount,
          Frequency: bill.frequency,
          "Monthly Equivalent Used": bill.monthlyEquivalent,
          "Historical Monthly Equivalent": bill.baseMonthlyEquivalent,
          "Start Date": bill.startDate,
          Description: bill.description || "",
          "Payment Status": bill.paymentStopped ? "Stopped" : "Active",
          "Last Payment Date": bill.lastPaymentDate || "",
        })),
        Payroll: allPayrollEmployees.map((emp) => ({
          Employee: emp.name,
          Status: emp.payrollStatus,
          "Staff Hourly Rate": emp.staffHourlyRate,
          "Override Hourly Rate": emp.overrideHourlyRate || "",
          "Hours Per Day": emp.hoursPerDay,
          "Flat Daily Pay Rate": emp.flatDailyRate || "",
          "Day Pay Used": emp.dailyPay,
          "Monthly Payroll Reserve": emp.hidden ? 0 : emp.monthlyPayroll,
          "Projected Month-To-Date": emp.projectedMTD,
          "Tech Daily Pay Days This Month": emp.actualDays,
          "Tech Daily Pay Actual Month-To-Date": emp.actualMTD,
        })),
        "Invoices This Month": currentMonthInvoices.map((invoice) => ({
          Date: invoiceDate(invoice),
          Status: invoice.status || "",
          "WO / Invoice": invoice.wo || invoice.invoiceNumber || invoice.id,
          Site: invoice.siteName || invoice.propertyName || "",
          Amount: invoiceAmount(invoice),
          Description: invoice.description || invoice.jobDescription || "",
        })),
        "Unpaid Invoices": income.unpaid.map((invoice) => ({
          Date: invoiceDate(invoice),
          Status: invoice.status || "",
          "WO / Invoice": invoice.wo || invoice.invoiceNumber || invoice.id,
          Site: invoice.siteName || invoice.propertyName || "",
          Amount: invoiceAmount(invoice),
          Paid: invoice.__paid,
          Balance: invoice.__balance,
        })),
        "Scheduled Visits": income.visits.map((v) => ({
          Date: v.date,
          WO: v.wo,
          Site: v.siteName,
          Fee: v.kind,
          Amount: v.amount,
        })),
        Debts: debts.map((debt) => ({
          Creditor: debt.creditorName,
          "Debt Type": debt.debtType,
          "Personal or Business": ownerLabel(debt.ownerType),
          Balance: toNum(debt.balance),
          "Minimum Payment": toNum(debt.minimumPayment),
          "Term Frequency": debtTermFrequencyLabel(debt.termFrequency),
          "Number of Payments": toNum(debt.termPayments),
          "Extra Payment": toNum(debt.extraPayment),
          "Scheduled Payment": debtScheduledPayment(debt),
          "Monthly Equivalent": debtMonthlyEquivalent(debt, workingDays),
          "Daily Needed": debtDailyEquivalent(debt, workingDays),
          "Target Payoff Date": debt.targetPayoffDate || "",
          "Days Until Target": debt.targetPayoffDate ? daysUntil(debt.targetPayoffDate) : "",
          Priority: debt.priority || "",
          Notes: debt.notes || "",
        })),
        "Bank Buckets": buckets.map((bucket) => ({
          "Bucket Name": bucket.bucketName,
          Bank: bucket.bankName || "",
          "Last 4": bucket.accountLast4 || "",
          "Personal or Business": ownerLabel(bucket.ownerType),
          "Target Percent": toNum(bucket.targetPercent),
          Purpose: bucket.purpose || "",
          "Suggested Transfer From Today Money": (toNum(todayMoney) * toNum(bucket.targetPercent)) / 100,
        })),
      },
      `Priority_Cash_Plan_${todayKey()}.xlsx`
    );
  };

  const parseBulkFile = async (file) => {
    const buffer = await file.arrayBuffer();
    const wb = XLSX.read(buffer);
    const readSheet = (name) => {
      const ws = wb.Sheets[name];
      return ws ? XLSX.utils.sheet_to_json(ws) : [];
    };

    const parsedDebts = readSheet("Debts").map((row) => ({
      creditorName: row.Creditor || "",
      debtType: row["Debt Type"] || "Credit Card",
      ownerType: String(row["Personal or Business"] || "business").toLowerCase() === "personal" ? "personal" : "business",
      balance: toNum(row.Balance),
      minimumPayment: toNum(row["Minimum Payment"]),
      termFrequency: debtTermFrequencyLabel(row["Term Frequency"] || row["Payment Frequency"] || "Monthly"),
      termPayments: toNum(row["Number of Payments"] || row["Term Payments"] || row["Term"]),
      extraPayment: toNum(row["Extra Payment"] || row["Additional Payment"]),
      targetPayoffDate: toDateKey(row["Target Payoff Date"]),
      priority: row.Priority || "Medium",
      notes: row.Notes || "",
    })).filter((row) => row.creditorName && row.balance);

    const parsedBuckets = readSheet("Bank Buckets").map((row) => ({
      bucketName: row["Bucket Name"] || "",
      bankName: row.Bank || "",
      accountLast4: String(row["Last 4"] || "").replace(/\D/g, "").slice(-4),
      ownerType: String(row["Personal or Business"] || "business").toLowerCase() === "personal" ? "personal" : "business",
      targetPercent: toNum(row["Target Percent"]),
      purpose: row.Purpose || "",
    })).filter((row) => row.bucketName);

    const parsedMonthly = readSheet("Monthly Bills").map((row) => ({
      vendorName: row.Vendor || "",
      billType: row["Bill Type"] || "Other",
      ownerType: String(row["Personal or Business"] || "business").toLowerCase() === "personal" ? "personal" : "business",
      amount: toNum(row.Amount),
      frequency: row.Frequency || "Monthly",
      startDate: toDateKey(row["Start Date"]) || todayKey(),
      description: row.Description || "",
      paymentStopped: String(row["Stop Payment"] || "No").toLowerCase().startsWith("y"),
      lastPaymentDate: toDateKey(row["Last Payment Date"]),
    })).filter((row) => row.vendorName && row.amount);

    setBulkPreview({ debts: parsedDebts, buckets: parsedBuckets, monthly: parsedMonthly });
    setBulkStatus(`Loaded ${parsedDebts.length} debt(s), ${parsedBuckets.length} bucket(s), and ${parsedMonthly.length} monthly bill(s). Review, then click Save Bulk Upload.`);
  };

  const saveBulkUpload = async () => {
    const { debts: debtRows, buckets: bucketRows, monthly: monthlyRows } = bulkPreview;
    if (!debtRows.length && !bucketRows.length && !monthlyRows.length) {
      alert("Upload the Cash Plan template first.");
      return;
    }
    if (!window.confirm("Save this bulk upload to Cash Plan and monthly bill sources?")) return;
    setSaving(true);
    try {
      for (const row of debtRows) {
        await addDoc(collection(db, "cashPlanDebts"), { ...row, createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
      }
      for (const row of bucketRows) {
        await addDoc(collection(db, "cashPlanBuckets"), { ...row, createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
      }
      for (const row of monthlyRows) {
        const cashPlanLinkId = `cashplan_bulk_${Date.now()}_${Math.random().toString(36).slice(2)}`;
        await Promise.all([
          addDoc(collection(db, "monthly_expenses"), {
            vendorId: "",
            vendorName: row.vendorName,
            billType: row.billType,
            billTypeOther: row.billType === "Other" ? row.description : "",
            description: row.description,
            amount: row.amount,
            frequency: row.frequency,
            startDate: row.startDate || todayKey(),
            ownerType: row.ownerType,
            paymentStopped: row.paymentStopped === true,
            lastPaymentDate: row.lastPaymentDate || "",
            source: "CashPlanDashboardBulkUpload",
            sourceCashPlanId: cashPlanLinkId,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          }),
          addDoc(collection(db, "monthlyBills"), {
            vendor: row.vendorName,
            amount: row.amount,
            billType: row.billType,
            description: row.description,
            effect: "subtract",
            date: row.startDate || todayKey(),
            frequency: row.frequency,
            ownerType: row.ownerType,
            paymentStopped: row.paymentStopped === true,
            lastPaymentDate: row.lastPaymentDate || "",
            source: "CashPlanDashboardBulkUpload",
            sourceCashPlanId: cashPlanLinkId,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          }),
        ]);
      }
      setBulkPreview({ debts: [], buckets: [], monthly: [] });
      setBulkStatus("Bulk upload saved.");
      await loadData();
    } finally {
      setSaving(false);
    }
  };

  if (!unlocked) {
    return (
      <div className="cashplan-page" style={{ padding: 20, maxWidth: 520, margin: "0 auto" }}>
        <h1>Cash Plan</h1>
        <div style={PAGE_INSTRUCTION_NOTE_STYLE}>
          PRIVATE PAGE: This page stores private debt, personal/business bill labels, and account bucket planning. Enter access code to open it.
        </div>
        <form onSubmit={unlock} style={cardStyle}>
          <label style={{ fontWeight: 800 }}>Access Code</label>
          <input
            type="password"
            value={accessInput}
            onChange={(e) => setAccessInput(e.target.value)}
            style={{ ...inputStyle, marginTop: 8 }}
            placeholder="Enter code"
          />
          {accessError && <p style={{ color: "#b91c1c", fontWeight: 700 }}>{accessError}</p>}
          <button type="submit" style={{ ...buttonStyle, marginTop: 10 }}>Unlock Cash Plan</button>
        </form>
      </div>
    );
  }

  const payrollGap = actualTechPayMTD - totals.employeeProjectedMTD;

  return (
    <div className="cashplan-page" style={{ padding: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
        <div>
          <h1 style={{ marginBottom: 4 }}>Cash Plan / Daily Money Target</h1>
          <p style={{ marginTop: 0 }}>Priority Pest Defense — plan daily revenue, payroll, monthly bills, private debts, and bank-account bucket transfers.</p>
        </div>
        <button type="button" style={dangerButtonStyle} onClick={lockPage}>Lock Page</button>
      </div>

      <div style={PAGE_INSTRUCTION_NOTE_STYLE}>
        This page reads monthly bills (monthly_expenses + monthlyBills), Staff pay, Tech Daily Pay, approved invoices, deposits,
        scheduled jobs, subcontractor pay, and your private debts and buckets. Stopping a monthly bill does not delete history; it
        only removes it from future targets after the last payment date. Payroll is projected from each employee's normal day
        pay on the Staff page (Daily Pay Rate, or Work Hourly Rate × Hours Per Day) × working days; the Tech Daily Pay actually
        saved this month is shown next to it and is never added on top.
      </div>

      {loading && <div style={{ color: "#334155", fontWeight: 700 }}>Loading cash plan data…</div>}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 12 }}>
        <div style={cardStyle}><strong>Business Daily Target</strong><div style={bigNumber}>{money(totals.businessDaily)}</div><small>Business bills + business debt, not payroll.</small></div>
        <div style={cardStyle}><strong>Subcontractors Paid This Month</strong><div style={bigNumber}>{money(subcontractorPaidThisMonth)}</div><small>From the shared subcontractorPay records.</small></div>
        <div style={cardStyle}><strong>Personal Daily Target</strong><div style={bigNumber}>{money(totals.personalDaily)}</div></div>
        <div style={cardStyle}><strong>Employee Daily Target</strong><div style={bigNumber}>{money(totals.employeeDaily)}</div><small>{activePayrollEmployees.length} active employee(s), projected day pay.</small></div>
        <div style={cardStyle}><strong>Total Daily Target</strong><div style={bigNumber}>{money(totals.allDaily)}</div><small>Business + personal + employee.</small></div>
        <div style={cardStyle}>
          <strong>Working Days / Month</strong>
          <input type="number" min="1" value={workingDays} onChange={(e) => setWorkingDays(e.target.value)} style={{ ...inputStyle, marginTop: 8 }} />
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: 12, marginTop: 12 }}>
        <div style={cardStyle}><strong>Business Monthly Need</strong><div>{money(totals.businessMonthly)}</div></div>
        <div style={cardStyle}><strong>Personal Monthly Need</strong><div>{money(totals.personalMonthly)}</div></div>
        <div style={cardStyle}><strong>Employee Monthly Payroll</strong><div>{money(totals.employeeMonthly)}</div><small>Projection</small></div>
        <div style={cardStyle}><strong>Total Monthly Need</strong><div>{money(totals.allMonthly)}</div></div>
        <div style={cardStyle}><strong>Invoices This Month</strong><div>{money(invoiceProgress.total)}</div><small>{invoiceProgress.count} approved/sent/paid invoice(s) dated this month</small></div>
        <div style={cardStyle}><strong>Remaining Daily Target</strong><div>{money(totals.remainingDailyNeed)}</div><small>After this month's invoices, over {invoiceProgress.remainingBusinessDays} business day(s) left.</small></div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: 12, marginTop: 12 }}>
        <div style={{ ...cardStyle, background: "#f0fdf4", borderColor: "#86efac" }}>
          <strong>Payroll: Projected vs Actual (month to date)</strong>
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}><span>Projected ({businessDaysSoFar} work day{businessDaysSoFar === 1 ? "" : "s"})</span><b>{money(totals.employeeProjectedMTD)}</b></div>
          <div style={{ display: "flex", justifyContent: "space-between" }}><span>Tech Daily Pay actual</span><b>{money(actualTechPayMTD)}</b></div>
          <small style={{ color: payrollGap > 0 ? "#b91c1c" : "#047857", fontWeight: 700 }}>
            {payrollGap > 0 ? `${money(payrollGap)} over projection` : `${money(-payrollGap)} under projection`}
          </small>
        </div>
        <div style={cardStyle}><strong>Unpaid Invoices (coming in)</strong><div>{money(income.unpaidTotal)}</div><small>{income.unpaid.length} invoice(s) not yet covered by payments</small></div>
        <div style={cardStyle}><strong>Scheduled Visits Rest of Month</strong><div>{money(income.visitsTotal)}</div><small>{income.visits.length} visit(s) from today to {monthEnd}, not completed yet</small></div>
        <div style={cardStyle}><strong>Still Needed After Scheduled Visits</strong><div>{money(totals.needAfterScheduled)}</div><small>Remaining monthly target minus scheduled visits.</small></div>
        <div style={cardStyle}><strong>Payments Received This Month</strong><div>{money(income.receivedThisMonth)}</div><small>From Deposits</small></div>
      </div>

      <div style={{ ...sectionStyle, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <button type="button" style={grayButtonStyle} onClick={() => setShowExcelTools((v) => !v)}>{showExcelTools ? "Hide Excel Tools" : "Show Excel Tools"}</button>
        <button type="button" style={grayButtonStyle} onClick={loadData}>Refresh Data</button>
        {bulkStatus && <span style={{ fontWeight: 700 }}>{bulkStatus}</span>}
      </div>

      {showExcelTools && (
        <div style={{ ...sectionStyle, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <button type="button" style={buttonStyle} onClick={downloadTemplate}>Download Excel Bulk Template</button>
          <label style={grayButtonStyle}>
            Upload Excel Template
            <input type="file" accept=".xlsx,.xls" onChange={(e) => e.target.files?.[0] && parseBulkFile(e.target.files[0])} style={{ display: "none" }} />
          </label>
          <button type="button" style={buttonStyle} onClick={saveBulkUpload} disabled={saving}>Save Bulk Upload</button>
          <button type="button" style={grayButtonStyle} onClick={downloadPlan}>Download Current Cash Plan</button>
          {(bulkPreview.debts.length || bulkPreview.buckets.length || bulkPreview.monthly.length) > 0 && (
            <strong>{bulkPreview.debts.length} debt(s), {bulkPreview.buckets.length} bucket(s), {bulkPreview.monthly.length} monthly bill(s) ready to save.</strong>
          )}
        </div>
      )}

      <div style={sectionStyle}>
        <h2>Money Coming In</h2>
        <p>Unpaid invoices are invoices on Approved Invoices that payments on Deposits don't cover yet. Scheduled visits are recurring and one-time jobs still on the schedule for the rest of this month, priced from the job's fee (initial fee for a first visit, otherwise the recurring fee). A visit that's been completed moves to the invoice list, so nothing is counted twice.</p>
        <button type="button" style={grayButtonStyle} onClick={() => setShowIncome((v) => !v)}>{showIncome ? "Hide Details" : "Show Details"}</button>
        {showIncome && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 12, marginTop: 10 }}>
            <div style={{ overflowX: "auto" }}>
              <h3 style={{ margin: "6px 0" }}>Unpaid Invoices ({money(income.unpaidTotal)})</h3>
              <table className="cashplan-table">
                <thead><tr><th>Date</th><th>Site</th><th>WO / Invoice</th><th>Status</th><th>Balance</th></tr></thead>
                <tbody>
                  {income.unpaid.map((inv) => (
                    <tr key={inv.id}>
                      <td>{invoiceDate(inv)}</td>
                      <td>{inv.siteName || inv.propertyName || ""}</td>
                      <td>{inv.invoiceNumber || inv.wo || inv.id}</td>
                      <td>{inv.status || "Draft"}</td>
                      <td>{money(inv.__balance)}</td>
                    </tr>
                  ))}
                  {!income.unpaid.length && <tr><td colSpan={5} style={{ textAlign: "center" }}>No unpaid invoices.</td></tr>}
                </tbody>
              </table>
            </div>
            <div style={{ overflowX: "auto" }}>
              <h3 style={{ margin: "6px 0" }}>Scheduled Visits Rest of Month ({money(income.visitsTotal)})</h3>
              <table className="cashplan-table">
                <thead><tr><th>Date</th><th>Site</th><th>WO</th><th>Fee</th><th>Amount</th></tr></thead>
                <tbody>
                  {income.visits.map((v) => (
                    <tr key={`${v.jobId}-${v.occurrenceKey}`}>
                      <td>{v.date}</td>
                      <td>{v.siteName}</td>
                      <td>{v.wo}</td>
                      <td>{v.kind}</td>
                      <td>{v.amount ? money(v.amount) : <span style={{ color: "#b45309", fontWeight: 700 }}>No fee set</span>}</td>
                    </tr>
                  ))}
                  {!income.visits.length && <tr><td colSpan={5} style={{ textAlign: "center" }}>No more visits scheduled this month.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <div style={sectionStyle}>
        <h2>Bank Account Buckets / Daily Transfer Plan</h2>
        <p>Enter your bank accounts or account buckets. Change fields first, then click Save so changes do not depend on autosave.</p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 8, marginBottom: 10 }}>
          <input style={inputStyle} placeholder="Money received today" value={todayMoney} onChange={(e) => setTodayMoney(e.target.value)} />
          <button type="button" style={grayButtonStyle} onClick={() => setShowBuckets((v) => !v)}>{showBuckets ? "Hide Buckets" : "Show Buckets"}</button>
        </div>
        {showBuckets && (
          <>
            <form onSubmit={addBucket} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 8 }}>
              <input style={inputStyle} placeholder="Bucket / Account Name" value={bucketForm.bucketName} onChange={(e) => setBucketForm((f) => ({ ...f, bucketName: e.target.value }))} />
              <input style={inputStyle} placeholder="Bank" value={bucketForm.bankName} onChange={(e) => setBucketForm((f) => ({ ...f, bankName: e.target.value }))} />
              <input style={inputStyle} placeholder="Last 4" value={bucketForm.accountLast4} onChange={(e) => setBucketForm((f) => ({ ...f, accountLast4: e.target.value.replace(/\D/g, "").slice(-4) }))} />
              <select style={inputStyle} value={bucketForm.ownerType} onChange={(e) => setBucketForm((f) => ({ ...f, ownerType: e.target.value }))}>{ownerOptions.map((o) => <option key={o} value={o}>{ownerLabel(o)}</option>)}</select>
              <input style={inputStyle} type="number" placeholder="Target %" value={bucketForm.targetPercent} onChange={(e) => setBucketForm((f) => ({ ...f, targetPercent: e.target.value }))} />
              <input style={inputStyle} placeholder="Purpose" value={bucketForm.purpose} onChange={(e) => setBucketForm((f) => ({ ...f, purpose: e.target.value }))} />
              <button style={buttonStyle} disabled={saving}>Add Bucket</button>
            </form>
            <div style={{ overflowX: "auto", marginTop: 10 }}>
              <table className="cashplan-table">
                <thead><tr><th>Bucket</th><th>Bank</th><th>Last 4</th><th>Type</th><th>Target %</th><th>Suggested From Today</th><th>Purpose</th><th>Actions</th></tr></thead>
                <tbody>
                  {buckets.map((bucket) => {
                    const key = `bucket-${bucket.id}`;
                    return (
                      <tr key={bucket.id}>
                        <td><input style={inputStyle} value={draftValue(key, bucket, "bucketName")} onChange={(e) => setDraftValue(key, "bucketName", e.target.value)} /></td>
                        <td><input style={inputStyle} value={draftValue(key, bucket, "bankName")} onChange={(e) => setDraftValue(key, "bankName", e.target.value)} /></td>
                        <td><input style={{ ...inputStyle, width: 90 }} value={draftValue(key, bucket, "accountLast4")} onChange={(e) => setDraftValue(key, "accountLast4", e.target.value.replace(/\D/g, "").slice(-4))} /></td>
                        <td><select style={inputStyle} value={draftValue(key, bucket, "ownerType", "business")} onChange={(e) => setDraftValue(key, "ownerType", e.target.value)}>{ownerOptions.map((o) => <option key={o} value={o}>{ownerLabel(o)}</option>)}</select></td>
                        <td><input style={{ ...inputStyle, width: 90 }} type="number" value={draftValue(key, bucket, "targetPercent", "")} onChange={(e) => setDraftValue(key, "targetPercent", e.target.value)} /></td>
                        <td>{money((toNum(todayMoney) * toNum(draftValue(key, bucket, "targetPercent", 0))) / 100)}</td>
                        <td><input style={inputStyle} value={draftValue(key, bucket, "purpose", "")} onChange={(e) => setDraftValue(key, "purpose", e.target.value)} /></td>
                        <td><div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}><button type="button" style={buttonStyle} onClick={() => saveBucketDraft(bucket)}>Save</button><button type="button" style={dangerButtonStyle} onClick={() => deleteBucket(bucket.id)}>Delete</button></div></td>
                      </tr>
                    );
                  })}
                  {!buckets.length && <tr><td colSpan={8} style={{ textAlign: "center" }}>No buckets added yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      <div style={sectionStyle}>
        <h2>Monthly Bills</h2>
        <p>Monthly bills are pulled from existing monthly bill sources (including the Expenses page). Stopped bills stay in the table for history but stop counting in future targets after the last payment date.</p>
        <button type="button" style={grayButtonStyle} onClick={() => setShowMonthly((v) => !v)}>{showMonthly ? "Hide Monthly Bills" : "Show Monthly Bills"}</button>
        {showMonthly && (
          <>
            <form onSubmit={addMonthlyBill} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 8, marginTop: 10 }}>
              <input style={inputStyle} placeholder="Vendor" value={monthlyForm.vendorName} onChange={(e) => setMonthlyForm((f) => ({ ...f, vendorName: e.target.value }))} />
              <select style={inputStyle} value={monthlyForm.billType} onChange={(e) => setMonthlyForm((f) => ({ ...f, billType: e.target.value }))}>{billTypes.map((b) => <option key={b}>{b}</option>)}</select>
              <select style={inputStyle} value={monthlyForm.ownerType} onChange={(e) => setMonthlyForm((f) => ({ ...f, ownerType: e.target.value }))}>{ownerOptions.map((o) => <option key={o} value={o}>{ownerLabel(o)}</option>)}</select>
              <input style={inputStyle} type="number" placeholder="Amount" value={monthlyForm.amount} onChange={(e) => setMonthlyForm((f) => ({ ...f, amount: e.target.value }))} />
              <select style={inputStyle} value={monthlyForm.frequency} onChange={(e) => setMonthlyForm((f) => ({ ...f, frequency: e.target.value }))}>{frequencyOptions.map((f) => <option key={f}>{f}</option>)}</select>
              <input style={inputStyle} type="date" value={monthlyForm.startDate} onChange={(e) => setMonthlyForm((f) => ({ ...f, startDate: e.target.value }))} />
              <select style={inputStyle} value={monthlyForm.paymentStopped ? "yes" : "no"} onChange={(e) => setMonthlyForm((f) => ({ ...f, paymentStopped: e.target.value === "yes" }))}><option value="no">Payment Active</option><option value="yes">Stop Payment</option></select>
              <input style={inputStyle} type="date" value={monthlyForm.lastPaymentDate} onChange={(e) => setMonthlyForm((f) => ({ ...f, lastPaymentDate: e.target.value }))} title="Last payment date" />
              <input style={inputStyle} placeholder="Description" value={monthlyForm.description} onChange={(e) => setMonthlyForm((f) => ({ ...f, description: e.target.value }))} />
              <button style={buttonStyle} disabled={saving}>Add Monthly Bill</button>
            </form>
            <div style={{ overflowX: "auto", marginTop: 10 }}>
              <table className="cashplan-table">
                <thead><tr><th>Source</th><th>Vendor</th><th>Type</th><th>Business/Personal</th><th>Amount</th><th>Frequency</th><th>Monthly Used</th><th>History Amount</th><th>Start Date</th><th>Description</th><th>Status</th><th>Last Payment</th><th>Actions</th></tr></thead>
                <tbody>
                  {allMonthlyBills.map((bill) => {
                    const key = `monthly-${bill.sourceCollection}-${bill.id}`;
                    return (
                      <tr key={`${bill.sourceCollection}-${bill.id}`} style={{ opacity: bill.paymentStopped && !bill.activeInCashPlan ? 0.65 : 1 }}>
                        <td>{bill.sourceCollection}</td>
                        <td><input style={{ ...inputStyle, minWidth: 160 }} value={draftValue(key, bill, "vendorName", "")} onChange={(e) => setDraftValue(key, "vendorName", e.target.value)} /></td>
                        <td><select style={{ ...inputStyle, minWidth: 140 }} value={draftValue(key, bill, "billType", "Other")} onChange={(e) => setDraftValue(key, "billType", e.target.value)}>{billTypes.map((b) => <option key={b}>{b}</option>)}{!billTypes.includes(draftValue(key, bill, "billType", "Other")) && <option>{draftValue(key, bill, "billType", "Other")}</option>}</select></td>
                        <td><select style={{ ...inputStyle, minWidth: 110 }} value={draftValue(key, bill, "ownerType", "business")} onChange={(e) => setDraftValue(key, "ownerType", e.target.value)}>{ownerOptions.map((o) => <option key={o} value={o}>{ownerLabel(o)}</option>)}</select></td>
                        <td><input style={{ ...inputStyle, width: 110 }} type="number" value={draftValue(key, bill, "amount", "")} onChange={(e) => setDraftValue(key, "amount", e.target.value)} /></td>
                        <td><select style={{ ...inputStyle, minWidth: 130 }} value={draftValue(key, bill, "frequency", "Monthly")} onChange={(e) => setDraftValue(key, "frequency", e.target.value)}>{frequencyOptions.map((f) => <option key={f}>{f}</option>)}</select></td>
                        <td>{bill.paymentStopped && !bill.activeInCashPlan ? <span style={{ color: "#991b1b", fontWeight: 800 }}>$0.00 stopped</span> : money(bill.monthlyEquivalent)}</td>
                        <td>{money(bill.baseMonthlyEquivalent)}</td>
                        <td><input style={{ ...inputStyle, minWidth: 135 }} type="date" value={draftValue(key, bill, "startDate", "")} onChange={(e) => setDraftValue(key, "startDate", e.target.value)} /></td>
                        <td><input style={{ ...inputStyle, minWidth: 220 }} value={draftValue(key, bill, "description", "")} onChange={(e) => setDraftValue(key, "description", e.target.value)} /></td>
                        <td>{bill.paymentStopped ? <strong style={{ color: "#b91c1c" }}>Stopped</strong> : <strong style={{ color: "#047857" }}>Active</strong>}</td>
                        <td><input style={{ ...inputStyle, minWidth: 135 }} type="date" value={draftValue(key, bill, "lastPaymentDate", "")} onChange={(e) => setDraftValue(key, "lastPaymentDate", e.target.value)} /></td>
                        <td><div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}><button type="button" style={buttonStyle} onClick={() => saveMonthlyDraft(bill)}>Save</button><button type="button" style={bill.paymentStopped ? buttonStyle : dangerButtonStyle} onClick={() => stopMonthlyBill(bill)}>{bill.paymentStopped ? "Restart" : "Stop Payment"}</button></div></td>
                      </tr>
                    );
                  })}
                  {!allMonthlyBills.length && <tr><td colSpan={13} style={{ textAlign: "center" }}>No monthly bills found.</td></tr>}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      <div style={sectionStyle}>
        <h2>Employee Payroll Reserve</h2>
        <p>
          Projection uses each employee's normal day pay from the Staff page — the Daily Pay Rate when set, otherwise Work Hourly
          Rate × Hours Per Day — times {totals.workdays} working days. An override hourly rate here replaces the Staff hourly rate
          for the plan only. "Actual" is the Tech Daily Pay saved so far this month; it is shown for comparison and never added to
          the targets. Subcontractors are left out (they're paid through Subcontractor Pay).
        </p>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 10 }}>
          <button type="button" style={grayButtonStyle} onClick={() => setShowPayroll((v) => !v)}>{showPayroll ? "Hide Employee Payroll Reserve" : "Show Employee Payroll Reserve"}</button>
          <button type="button" style={grayButtonStyle} onClick={() => setShowHiddenEmployees((v) => !v)}>{showHiddenEmployees ? "Hide Employees Marked Hidden" : "Show Employees Marked Hidden"}</button>
        </div>
        {showPayroll && (
          <div style={{ overflowX: "auto" }}>
            <table className="cashplan-table">
              <thead><tr><th>Employee</th><th>Status</th><th>Staff Hourly Rate</th><th>Override Hourly Rate</th><th>Hours / Day</th><th>Day Pay</th><th>Monthly Projection</th><th>Daily Target</th><th>Projected MTD</th><th>Actual MTD (days)</th><th>Actions</th></tr></thead>
              <tbody>
                {payrollEmployees.map((emp) => {
                  const key = `emp-${emp.id}`;
                  const draftOverrideRaw = draftValue(key, emp, "overrideHourlyRate", emp.overrideHourlyRate || "");
                  const draftOverride = toNum(draftOverrideRaw);
                  const user = usersById[emp.id] || {};
                  const dayPay = projectedDailyPay(user, draftOverride);
                  const draftHidden = emp.hidden === true;
                  const monthlyGross = dayPay * totals.workdays;
                  const dailyTarget = draftHidden ? 0 : dayPay;
                  return (
                    <tr key={emp.id} style={{ opacity: draftHidden ? 0.55 : 1 }}>
                      <td>{emp.name}</td>
                      <td>{draftHidden ? "Hidden" : "Active"}</td>
                      <td>{money(emp.staffHourlyRate)}</td>
                      <td><input style={{ ...inputStyle, width: 130 }} type="number" step="0.01" placeholder="Blank = Staff rate" value={draftOverrideRaw} onChange={(e) => setDraftValue(key, "overrideHourlyRate", e.target.value)} /></td>
                      <td>{emp.flatDailyRate > 0 && !draftOverride ? "flat day rate" : emp.hoursPerDay}</td>
                      <td>{money(dayPay)}</td>
                      <td>{draftHidden ? "$0.00 hidden" : money(monthlyGross)}</td>
                      <td>{money(dailyTarget)}</td>
                      <td>{money(emp.projectedMTD)}</td>
                      <td>{money(emp.actualMTD)} ({emp.actualDays})</td>
                      <td><div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}><button type="button" style={buttonStyle} onClick={() => saveEmployeeDraft(emp)}>Save Rate</button><button type="button" style={draftHidden ? buttonStyle : dangerButtonStyle} onClick={() => setEmployeeHiddenNow(emp, !emp.hidden)}>{emp.hidden ? "Show in Reserve" : "Hide Employee"}</button></div></td>
                    </tr>
                  );
                })}
                {!payrollEmployees.length && <tr><td colSpan={11} style={{ textAlign: "center" }}>No employees found in Staff.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div style={sectionStyle}>
        <h2>Private Debt / Vendor Payoff Plan</h2>
        <p>Use this for private credit cards, debts, vendor balances, and payoff targets. Enter a balance, choose Monthly or Weekly, enter the number of payments, and the page calculates the scheduled payment. Example: $10,000 monthly for 48 payments = about $208.33/month before any extra payment. Change fields first, then click Save.</p>
        <button type="button" style={grayButtonStyle} onClick={() => setShowDebts((v) => !v)}>{showDebts ? "Hide Debts" : "Show Debts"}</button>
        {showDebts && (
          <>
            <form onSubmit={addDebt} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 8, marginTop: 10 }}>
              <input style={inputStyle} placeholder="Creditor / Vendor" value={debtForm.creditorName} onChange={(e) => setDebtForm((f) => ({ ...f, creditorName: e.target.value }))} />
              <select style={inputStyle} value={debtForm.debtType} onChange={(e) => setDebtForm((f) => ({ ...f, debtType: e.target.value }))}>{debtTypes.map((d) => <option key={d}>{d}</option>)}</select>
              <select style={inputStyle} value={debtForm.ownerType} onChange={(e) => setDebtForm((f) => ({ ...f, ownerType: e.target.value }))}>{ownerOptions.map((o) => <option key={o} value={o}>{ownerLabel(o)}</option>)}</select>
              <input style={inputStyle} type="number" placeholder="Balance" value={debtForm.balance} onChange={(e) => setDebtForm((f) => ({ ...f, balance: e.target.value }))} />
              <input style={inputStyle} type="number" placeholder="Minimum Payment" value={debtForm.minimumPayment} onChange={(e) => setDebtForm((f) => ({ ...f, minimumPayment: e.target.value }))} />
              <select style={inputStyle} value={debtForm.termFrequency} onChange={(e) => setDebtForm((f) => ({ ...f, termFrequency: e.target.value }))}><option>Monthly</option><option>Weekly</option></select>
              <input style={inputStyle} type="number" placeholder="# of payments / term" value={debtForm.termPayments} onChange={(e) => setDebtForm((f) => ({ ...f, termPayments: e.target.value }))} />
              <input style={inputStyle} type="number" placeholder="Extra payment" value={debtForm.extraPayment} onChange={(e) => setDebtForm((f) => ({ ...f, extraPayment: e.target.value }))} />
              <input style={inputStyle} type="date" value={debtForm.targetPayoffDate} onChange={(e) => setDebtForm((f) => ({ ...f, targetPayoffDate: e.target.value }))} />
              <select style={inputStyle} value={debtForm.priority} onChange={(e) => setDebtForm((f) => ({ ...f, priority: e.target.value }))}><option>High</option><option>Medium</option><option>Low</option></select>
              <input style={inputStyle} placeholder="Notes" value={debtForm.notes} onChange={(e) => setDebtForm((f) => ({ ...f, notes: e.target.value }))} />
              <button style={buttonStyle} disabled={saving}>Add Debt</button>
            </form>
            <div style={{ overflowX: "auto", marginTop: 10 }}>
              <table className="cashplan-table">
                <thead><tr><th>Creditor</th><th>Type</th><th>Business/Personal</th><th>Balance</th><th>Minimum</th><th>Term</th><th># Payments</th><th>Extra</th><th>Scheduled Payment</th><th>Monthly Equivalent</th><th>Daily Needed</th><th>Target Payoff</th><th>Days Left</th><th>Priority</th><th>Notes</th><th>Actions</th></tr></thead>
                <tbody>
                  {debts.map((debt) => {
                    const key = `debt-${debt.id}`;
                    const balance = toNum(draftValue(key, debt, "balance", debt.balance));
                    const termFrequency = draftValue(key, debt, "termFrequency", debt.termFrequency || "Monthly");
                    const termPayments = toNum(draftValue(key, debt, "termPayments", debt.termPayments || ""));
                    const minimumPayment = toNum(draftValue(key, debt, "minimumPayment", debt.minimumPayment || ""));
                    const extraPayment = toNum(draftValue(key, debt, "extraPayment", debt.extraPayment || ""));
                    const draftDebt = { ...debt, balance, minimumPayment, termFrequency, termPayments, extraPayment };
                    const scheduledPayment = debtScheduledPayment(draftDebt);
                    const monthlyEquivalent = debtMonthlyEquivalent(draftDebt, workingDays);
                    const dailyNeeded = debtDailyEquivalent(draftDebt, workingDays);
                    const payoffDate = draftValue(key, debt, "targetPayoffDate", debt.targetPayoffDate || "");
                    const daysLeft = payoffDate ? daysUntil(payoffDate) : "";
                    return (
                      <tr key={debt.id}>
                        <td><input style={inputStyle} value={draftValue(key, debt, "creditorName", "")} onChange={(e) => setDraftValue(key, "creditorName", e.target.value)} /></td>
                        <td><select style={inputStyle} value={draftValue(key, debt, "debtType", "Credit Card")} onChange={(e) => setDraftValue(key, "debtType", e.target.value)}>{debtTypes.map((d) => <option key={d}>{d}</option>)}</select></td>
                        <td><select style={inputStyle} value={draftValue(key, debt, "ownerType", "business")} onChange={(e) => setDraftValue(key, "ownerType", e.target.value)}>{ownerOptions.map((o) => <option key={o} value={o}>{ownerLabel(o)}</option>)}</select></td>
                        <td><input style={{ ...inputStyle, width: 110 }} type="number" value={draftValue(key, debt, "balance", "")} onChange={(e) => setDraftValue(key, "balance", e.target.value)} /></td>
                        <td><input style={{ ...inputStyle, width: 110 }} type="number" value={draftValue(key, debt, "minimumPayment", "")} onChange={(e) => setDraftValue(key, "minimumPayment", e.target.value)} /></td>
                        <td><select style={{ ...inputStyle, minWidth: 105 }} value={termFrequency} onChange={(e) => setDraftValue(key, "termFrequency", e.target.value)}><option>Monthly</option><option>Weekly</option></select></td>
                        <td><input style={{ ...inputStyle, width: 100 }} type="number" value={draftValue(key, debt, "termPayments", "")} onChange={(e) => setDraftValue(key, "termPayments", e.target.value)} /></td>
                        <td><input style={{ ...inputStyle, width: 100 }} type="number" value={draftValue(key, debt, "extraPayment", "")} onChange={(e) => setDraftValue(key, "extraPayment", e.target.value)} /></td>
                        <td>{money(scheduledPayment)} / {debtTermFrequencyLabel(termFrequency).toLowerCase()}</td>
                        <td>{money(monthlyEquivalent)}</td>
                        <td>{money(dailyNeeded)}</td>
                        <td><input style={inputStyle} type="date" value={toDateKey(payoffDate)} onChange={(e) => setDraftValue(key, "targetPayoffDate", e.target.value)} /></td>
                        <td>{daysLeft || ""}</td>
                        <td><select style={inputStyle} value={draftValue(key, debt, "priority", "Medium")} onChange={(e) => setDraftValue(key, "priority", e.target.value)}><option>High</option><option>Medium</option><option>Low</option></select></td>
                        <td><input style={inputStyle} value={draftValue(key, debt, "notes", "")} onChange={(e) => setDraftValue(key, "notes", e.target.value)} /></td>
                        <td><div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}><button type="button" style={buttonStyle} onClick={() => saveDebtDraft(debt)}>Save</button><button type="button" style={dangerButtonStyle} onClick={() => deleteDebt(debt.id)}>Delete</button></div></td>
                      </tr>
                    );
                  })}
                  {!debts.length && <tr><td colSpan={16} style={{ textAlign: "center" }}>No private debts added yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
